<template>
  <div>
    <v-container fluid>
      <v-row class="ma-0 my-4 align-center justify-space-between">
        <h1 class="text-h5 font-weight-bold">Clínicas de reembolso</h1>
      </v-row>

      <v-row class="ma-0 mb-4 align-center filters-row">
        <v-text-field
          v-model="search"
          class="search-field"
          prepend-inner-icon="mdi-magnify"
          label="Buscar por nombre, razón social o RUT/RUC"
          outlined
          dense
          clearable
          hide-details
          @input="onFilterChange"
        ></v-text-field>
        <v-text-field
          v-model="county"
          class="county-field"
          prepend-inner-icon="mdi-map-marker"
          label="Comuna / distrito"
          outlined
          dense
          clearable
          hide-details
          @input="onFilterChange"
        ></v-text-field>
        <v-select
          v-model="countryId"
          class="country-field"
          :items="countries"
          :loading="loadingCountries"
          prepend-inner-icon="mdi-earth"
          label="País"
          outlined
          dense
          clearable
          hide-details
          @change="onFilterChange"
        ></v-select>
        <v-checkbox
          v-model="multipleRutsOnly"
          class="mt-0 pt-0"
          label="Solo con más de un RUT/RUC"
          hide-details
          @change="onFilterChange"
        ></v-checkbox>
      </v-row>

      <v-card outlined>
        <v-data-table
          :headers="headers"
          :items="clinics"
          item-key="clinicId"
          :loading="loading"
          :options.sync="options"
          :server-items-length="total"
          :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
          must-sort
          no-data-text="No hay clínicas con reembolsos para esta búsqueda."
        >
          <template v-slot:item.centerName="{ item }">
            <div class="font-weight-medium">{{ item.centerName }}</div>
            <div v-if="item.businessName" class="text-caption grey--text">{{ item.businessName }}</div>
          </template>
          <template v-slot:item.ruts="{ item }">
            <div v-for="(rut, index) in item.ruts" :key="rut">
              {{ displayRut(rut, item.countryId) }}
              <v-chip v-if="index === 0 && item.ruts.length > 1" x-small class="ml-1">principal</v-chip>
            </div>
          </template>
          <template v-slot:item.county="{ item }">
            {{ item.county || '—' }}
          </template>
          <template v-slot:item.countryId="{ item }">
            {{ countryName(item.countryId) }}
          </template>
          <template v-slot:item.actions="{ item }">
            <v-chip v-if="item.isPetmedClinic" x-small color="purple lighten-4" class="mr-2">PetMed</v-chip>
            <v-btn fab depressed outlined x-small color="primary" @click="openEdit(item)">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-tooltip top :disabled="!item.isPetmedClinic">
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on">
                  <v-btn
                    fab
                    depressed
                    outlined
                    x-small
                    color="error"
                    class="ml-2"
                    :disabled="item.isPetmedClinic"
                    @click="openDelete(item)"
                  >
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </span>
              </template>
              <span>Las clínicas PetMed no se pueden eliminar desde aquí</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <v-dialog v-model="showEdit" persistent max-width="640" @keydown.esc="closeEdit">
      <v-card v-if="form">
        <v-card-title class="text-h6 font-weight-bold">Editar clínica</v-card-title>
        <v-card-text>
          <v-alert v-if="editing && editing.isPetmedClinic" type="info" dense outlined class="mb-4">
            Esta clínica también es una clínica PetMed: el nombre y el RUT cambiarán también allí.
          </v-alert>
          <v-alert type="warning" dense outlined class="mb-4" icon="mdi-sync">
            Los cambios se aplican también a los {{ editing && editing.refundsCount }} reembolsos de esta clínica.
          </v-alert>

          <v-text-field v-model="form.centerName" label="Nombre del centro *" outlined dense></v-text-field>
          <v-text-field v-model="form.businessName" label="Razón social" outlined dense></v-text-field>
          <v-text-field v-model="form.county" label="Comuna / distrito" outlined dense></v-text-field>
          <v-select
            v-model="form.countryId"
            :items="countries"
            :loading="loadingCountries"
            label="País"
            outlined
            dense
            clearable
            @change="form.ruts.forEach(formatRutRow)"
          ></v-select>

          <div class="text-subtitle-2 font-weight-bold mb-2">RUT / RUC</div>
          <div v-for="(row, index) in form.ruts" :key="row.key" class="d-flex align-center rut-row">
            <v-text-field
              v-model="row.rut"
              :label="index === 0 ? 'RUT/RUC principal *' : 'RUT/RUC adicional'"
              outlined
              dense
              hide-details
              class="mb-2"
              @blur="formatRutRow(row)"
            ></v-text-field>
            <v-tooltip v-if="index > 0" top>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small class="ml-1" v-bind="attrs" v-on="on" @click="makePrimary(index)">
                  <v-icon small>mdi-star-outline</v-icon>
                </v-btn>
              </template>
              <span>Marcar como principal</span>
            </v-tooltip>
            <v-btn icon small class="ml-1" :disabled="form.ruts.length === 1" @click="removeRut(index)">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </div>
          <v-btn text small color="primary" @click="addRut">
            <v-icon left small>mdi-plus</v-icon>Agregar RUT/RUC
          </v-btn>

          <v-alert v-if="formError" type="error" dense outlined class="mt-4 mb-0">{{ formError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="saving" @click="closeEdit">Cancelar</v-btn>
          <v-btn color="primary" depressed :loading="saving" @click="save">Guardar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDelete" persistent max-width="560" @keydown.esc="closeDelete">
      <v-card v-if="deleting">
        <v-card-title class="text-h6 font-weight-bold">Eliminar clínica</v-card-title>
        <v-card-text>
          <p class="mb-4">
            Vas a eliminar <strong>{{ deleting.centerName }}</strong>, que tiene
            {{ deleting.refundsCount }} reembolso(s). ¿Qué hacemos con ellos?
          </p>
          <v-radio-group v-model="deleteMode" class="mt-0" hide-details>
            <v-radio value="unlink" label="Desvincularlos (conservan el nombre, RUT/RUC y comuna registrados)"></v-radio>
            <v-radio value="merge" label="Fusionar en otra clínica (duplicado)"></v-radio>
          </v-radio-group>
          <v-autocomplete
            v-if="deleteMode === 'merge'"
            v-model="mergeTarget"
            class="mt-4"
            :items="mergeCandidates"
            :loading="loadingMergeCandidates"
            :search-input.sync="mergeSearch"
            item-text="centerName"
            item-value="clinicId"
            return-object
            no-filter
            hide-no-data
            outlined
            dense
            label="Clínica destino *"
            prepend-inner-icon="mdi-magnify"
          >
            <template v-slot:item="{ item }">
              <v-list-item-content>
                <v-list-item-title>{{ item.centerName }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ displayRut(item.ruts[0], item.countryId) }} · {{ item.county || '—' }} ·
                  {{ item.refundsCount }} reembolso(s)
                </v-list-item-subtitle>
              </v-list-item-content>
            </template>
          </v-autocomplete>
          <v-alert v-if="deleteMode === 'merge' && mergeTarget" type="info" dense outlined class="mb-0">
            Los reembolsos y los RUT/RUC de esta clínica pasarán a {{ mergeTarget.centerName }}.
          </v-alert>
          <v-alert v-if="deleteError" type="error" dense outlined class="mt-4 mb-0">{{ deleteError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="removing" @click="closeDelete">Cancelar</v-btn>
          <v-btn
            color="error"
            depressed
            :loading="removing"
            :disabled="deleteMode === 'merge' && !mergeTarget"
            @click="confirmDelete"
          >
            Eliminar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="4000">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import RefundClinicsService from '@/services/refund-clinics.service';
import CountryService from '@/services/countries.service';
import RutUtils from '@/utils/rut.utils';

const SEARCH_DEBOUNCE_MS = 400;

// Códigos que devuelve el backend (124-refund-clinics-maintainer) → mensaje para VetOps.
const ERROR_MESSAGES = {
  CENTER_NAME_REQUIRED: 'El nombre del centro es obligatorio.',
  CLINIC_RUT_REQUIRED: 'La clínica debe tener al menos un RUT/RUC.',
  INVALID_CLINIC_RUT: 'Hay un RUT/RUC inválido: debe ser un RUT chileno con dígito verificador correcto o un RUC de 11 dígitos.',
  DUPLICATE_CLINIC_RUT: 'Hay un RUT/RUC repetido.',
  UNKNOWN_PREVIOUS_CLINIC_RUT: 'La clínica cambió mientras la editabas. Recarga e intenta de nuevo.',
  TOO_MANY_CLINIC_RUTS: 'Una clínica puede tener como máximo 10 RUT/RUC.',
  INVALID_COUNTRY: 'El país elegido no existe.'
};

const DELETE_ERROR_MESSAGES = {
  MERGE_TARGET_NOT_FOUND: 'La clínica destino ya no existe.',
  CANNOT_MERGE_INTO_ITSELF: 'Elige una clínica destino distinta.',
  TOO_MANY_CLINIC_RUTS: 'La clínica destino quedaría con más de 10 RUT/RUC.'
};

let rowKey = 0;

export default {
  name: 'RefundClinics',
  data() {
    return {
      // Solo las columnas que el backend sabe ordenar (centerName, county, refundsCount).
      headers: [
        { text: 'Clínica', value: 'centerName' },
        { text: 'RUT / RUC', value: 'ruts', sortable: false },
        { text: 'Comuna / distrito', value: 'county' },
        { text: 'País', value: 'countryId', sortable: false },
        { text: 'Reembolsos', value: 'refundsCount', align: 'end' },
        { text: '', value: 'actions', align: 'end', sortable: false }
      ],
      clinics: [],
      total: 0,
      loading: false,
      search: '',
      county: '',
      // Filtro por país de la clínica. Vacío = todos.
      countryId: null,
      countries: [],
      loadingCountries: false,
      multipleRutsOnly: false,
      searchTimer: null,
      options: { page: 1, itemsPerPage: 20, sortBy: ['refundsCount'], sortDesc: [true] },
      showEdit: false,
      editing: null,
      form: null,
      formError: '',
      saving: false,
      showDelete: false,
      deleting: null,
      deleteMode: 'unlink',
      mergeTarget: null,
      mergeSearch: '',
      mergeCandidates: [],
      loadingMergeCandidates: false,
      mergeSearchTimer: null,
      deleteError: '',
      removing: false,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: ''
    };
  },
  watch: {
    options: {
      handler() {
        this.fetchClinics();
      },
      deep: true
    },
    mergeSearch(text) {
      clearTimeout(this.mergeSearchTimer);
      if (!text || (this.mergeTarget && text === this.mergeTarget.centerName)) return;
      this.mergeSearchTimer = setTimeout(() => this.searchMergeCandidates(text), SEARCH_DEBOUNCE_MS);
    }
  },
  created() {
    this.loadCountries();
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
    clearTimeout(this.mergeSearchTimer);
  },
  methods: {
    async loadCountries() {
      this.loadingCountries = true;
      try {
        const res = await CountryService.getAllCountries(true);
        this.countries = (res.data || []).map((c) => ({ text: c.name, value: c.id }));
      } catch (error) {
        this.notify('No se pudieron cargar los países.', 'error');
      }
      this.loadingCountries = false;
    },
    countryName(countryId) {
      const country = this.countries.find((c) => c.value === countryId);
      return country ? country.text : '—';
    },
    // Se guardan y buscan sin puntos ni guion (lo limpia el backend); se muestran
    // con el formato del país de la clínica.
    displayRut(rut, countryId) {
      const country = this.countries.find((c) => c.value === countryId);
      return RutUtils.formatClinicTaxId(rut, country && country.text);
    },
    formatRutRow(row) {
      if (row.rut && row.rut.trim()) row.rut = this.displayRut(row.rut, this.form.countryId);
    },
    async fetchClinics() {
      this.loading = true;
      const [orderBy] = this.options.sortBy || [];
      const [sortDesc] = this.options.sortDesc || [];
      const res = await RefundClinicsService.search({
        search: (this.search || '').trim(),
        county: (this.county || '').trim(),
        countryId: this.countryId,
        multipleRutsOnly: this.multipleRutsOnly,
        orderBy: orderBy || 'refundsCount',
        orderDirection: sortDesc ? 'desc' : 'asc',
        page: this.options.page,
        pageSize: this.options.itemsPerPage
      });
      this.loading = false;
      if (res.status !== 'ok') {
        this.notify('No se pudieron cargar las clínicas.', 'error');
        return;
      }
      this.clinics = res.data;
      this.total = res.meta.total;
    },
    onFilterChange() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.options.page === 1) this.fetchClinics();
        else this.options = { ...this.options, page: 1 };
      }, SEARCH_DEBOUNCE_MS);
    },
    openEdit(clinic) {
      this.editing = clinic;
      this.form = {
        centerName: clinic.centerName,
        businessName: clinic.businessName || '',
        county: clinic.county || '',
        countryId: clinic.countryId || null,
        // previousRut identifica qué RUT se está corrigiendo, para que los reembolsos lo sigan.
        ruts: clinic.ruts.map((rut) => ({
          key: ++rowKey,
          rut: this.displayRut(rut, clinic.countryId),
          previousRut: rut
        }))
      };
      this.formError = '';
      this.showEdit = true;
    },
    closeEdit() {
      if (this.saving) return;
      this.showEdit = false;
      this.editing = null;
      this.form = null;
    },
    addRut() {
      this.form.ruts.push({ key: ++rowKey, rut: '', previousRut: null });
    },
    removeRut(index) {
      this.form.ruts.splice(index, 1);
    },
    makePrimary(index) {
      const [row] = this.form.ruts.splice(index, 1);
      this.form.ruts.unshift(row);
    },
    buildPayload() {
      return {
        centerName: this.form.centerName,
        businessName: this.form.businessName || null,
        county: this.form.county || null,
        countryId: this.form.countryId || null,
        ruts: this.form.ruts
          .filter((row) => row.rut && row.rut.trim())
          .map((row) => (row.previousRut ? { rut: row.rut, previousRut: row.previousRut } : { rut: row.rut }))
      };
    },
    errorMessage(res) {
      if (res.code === 'CLINIC_RUT_TAKEN') return res.message;
      if (res.httpCode === 404) return 'La clínica ya no existe.';
      return ERROR_MESSAGES[res.message] || 'No se pudo guardar la clínica.';
    },
    async save() {
      this.saving = true;
      this.formError = '';
      const res = await RefundClinicsService.update(this.editing.clinicId, this.buildPayload());
      this.saving = false;
      if (res.status !== 'ok') {
        this.formError = this.errorMessage(res);
        return;
      }
      this.closeEdit();
      this.notify('Clínica actualizada.', 'success');
      this.fetchClinics();
    },
    openDelete(clinic) {
      this.deleting = clinic;
      this.deleteMode = 'unlink';
      this.mergeTarget = null;
      this.mergeCandidates = [];
      this.deleteError = '';
      this.showDelete = true;
    },
    closeDelete() {
      if (this.removing) return;
      this.showDelete = false;
      this.deleting = null;
    },
    async searchMergeCandidates(text) {
      this.loadingMergeCandidates = true;
      const res = await RefundClinicsService.search({
        search: text.trim(),
        orderBy: 'refundsCount',
        orderDirection: 'desc',
        page: 1,
        pageSize: 10
      });
      this.loadingMergeCandidates = false;
      if (res.status !== 'ok') return;
      this.mergeCandidates = res.data.filter((c) => c.clinicId !== this.deleting.clinicId);
    },
    deleteErrorMessage(res) {
      if (res.code === 'CLINIC_IN_USE') return res.message;
      if (res.httpCode === 404) return 'La clínica ya no existe.';
      return DELETE_ERROR_MESSAGES[res.message] || 'No se pudo eliminar la clínica.';
    },
    async confirmDelete() {
      this.removing = true;
      this.deleteError = '';
      const mergeIntoClinicId = this.deleteMode === 'merge' ? this.mergeTarget.clinicId : null;
      const res = await RefundClinicsService.remove(this.deleting.clinicId, mergeIntoClinicId);
      this.removing = false;
      if (res.status !== 'ok') {
        this.deleteError = this.deleteErrorMessage(res);
        return;
      }
      this.closeDelete();
      this.notify(mergeIntoClinicId ? 'Clínica fusionada.' : 'Clínica eliminada.', 'success');
      this.fetchClinics();
    },
    notify(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    }
  }
};
</script>

<style scoped>
.filters-row {
  gap: 16px;
}
.search-field {
  max-width: 420px;
}
.county-field {
  max-width: 260px;
}
.country-field {
  max-width: 200px;
}
.rut-row {
  gap: 4px;
}
</style>
